import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { setAccountSettingsTab } from "store/tempDataSlise"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconAccountSettings } from "assets/svg/modal-icon-account-settings.svg"

import { checkRoleType } from "utils/checkRoleType"
import { AccountSettingsThemes } from "modals/AccountSettingsThemes"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import TabsWrapper, { TabItem } from "components/common/TabsWrapper"

import { AccountSettingsAdditionalInfo } from "../AccountSettingsAdditionalInfo"
import { AccountSettingsIntegration } from "../AccountSettingsIntegration"
import { AccountSettingsNotifications } from "../AccountSettingsNotifications"
import { AccountSettingsPassword } from "../AccountSettingsPassword"
import { AccountSettingsProfile } from "../AccountSettingsProfile"
import AccountSettingsBilling from "./AccountSettingsBilling"

import styles from "./index.module.scss"

const StyledTabsWrapper = styled(TabsWrapper)`
  margin-bottom: 0;

  > div {
    flex: 1;
  }
`

export enum AccountSettingsTabs {
  PROFILE = "profile",
  ADDITIONAL_INFO = "additional-info",
  BILLING = "billing",
  PASSWORD = "password",
  INTEGRATION = "integration",
  THEMES = "themes",
  NOTIFICATIONS = "notifications",
}

interface Props {
  onCancel: () => void
}

export const AccountSettingsModal = ({ onCancel }: Props) => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const accountSettingsTab = useAppSelector(state => state.tempData.accountSettingsTab)
  const current_subscription = userProfile?.contractor?.subscription_plan
  const hasEPlusPlan = current_subscription === SubscriptionPlan.E_PLUS
  const tabs: TabItem[] = useMemo(
    () => [
      { id: AccountSettingsTabs.PROFILE, label: "Profile" },
      { id: AccountSettingsTabs.ADDITIONAL_INFO, label: "Additional information" },
      { id: AccountSettingsTabs.BILLING, label: "Billing" },
      { id: AccountSettingsTabs.PASSWORD, label: "Password" },
      { id: AccountSettingsTabs.INTEGRATION, label: "Integration" },
      ...(hasEPlusPlan ? [{ id: AccountSettingsTabs.THEMES, label: "Themes" }] : []),
      { id: AccountSettingsTabs.NOTIFICATIONS, label: "Notifications Settings" },
    ],
    [hasEPlusPlan],
  )
  const tabIndex = tabs.findIndex(tab => tab.id === accountSettingsTab)
  const [currentTab, setCurrentTab] = useState<number>(tabIndex !== -1 ? tabIndex : 0)
  const userRole = checkRoleType(userProfile?.type)
  const isTechnician = userRole === "contractor-technician"
  const isEngineer = userRole === "contractor-engineer"
  const shouldHideTabs = isTechnician || isEngineer

  useEffect(() => {
    if (accountSettingsTab !== undefined) {
      dispatch(setAccountSettingsTab(undefined))
    }
  }, [accountSettingsTab, dispatch])

  const renderCurrentView = () => {
    if (shouldHideTabs) {
      return <AccountSettingsNotifications />
    }

    const tab = tabs[currentTab]
    switch (tab.id) {
      case "additional-info":
        return <AccountSettingsAdditionalInfo onCancel={onCancel} />
      case "password":
        return <AccountSettingsPassword onCancel={onCancel} />
      case "integration":
        return <AccountSettingsIntegration />
      case "billing":
        return <AccountSettingsBilling />
      case "themes":
        return <AccountSettingsThemes />
      case "notifications":
        return <AccountSettingsNotifications />
      default:
        return <AccountSettingsProfile onCancel={onCancel} />
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout width="880px" isWithoutFooter={true} onCancel={onCancel}>
          <ModalHeader title={`${shouldHideTabs ? "Notifications" : "Account"} Settings`}>
            <IconAccountSettings />
          </ModalHeader>
          <div>
            {!shouldHideTabs && (
              <div className={styles.modalTabsWrapper}>
                <StyledTabsWrapper tabs={tabs} currentTab={currentTab} onTabChange={setCurrentTab} expandable={true} />
              </div>
            )}
            {renderCurrentView()}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
